// ---------------------------------------------------------------
// Build a print-ready rental document (open in a new window → the browser's
// Print dialog lets the user "Save as PDF" or print). Includes every rental
// detail, the payment breakdown, the signed agreement (terms + liability +
// license photo + signature), and all the car photos taken.
// ---------------------------------------------------------------
import { agreementTerms, liabilityClauses, money } from './rentalContract';

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const PHOTO_LABELS = { front: 'Front', left: 'Left', right: 'Right', back: 'Back', inside0: 'Interior 1', inside1: 'Interior 2', inside2: 'Interior 3', inside3: 'Interior 4' };

// data: { company, ctx, totals, rental, licensePhoto, signature, signedAt, carPhotos }
export function openRentalPdf(data) {
  const { company = 'Vehicle Rental', ctx = {}, totals = {}, rental = {}, licensePhoto, customerPhoto, signature, signedAt, carPhotos = {} } = data;

  const termRows = agreementTerms(ctx)
    .map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`).join('');

  const chargeRows = [];
  chargeRows.push(['Base charge', money(totals.base)]);
  if (totals.overageCharge) chargeRows.push([`Overage (${Math.round(totals.overageMiles)} mi × ${money(ctx.overageRate)})`, money(totals.overageCharge)]);
  if (totals.tollCharge) chargeRows.push([`Tolls (${totals.tollCount} × ${money(totals.tollRate)})`, money(totals.tollCharge)]);
  chargeRows.push(['Total', money(totals.total)]);
  if (totals.paid) chargeRows.push(['Paid', `− ${money(totals.paid)}`]);
  chargeRows.push(['Balance due', money(totals.balanceDue)]);
  if (ctx.deposit) chargeRows.push(['Security deposit held', money(ctx.deposit)]);
  if (rental.depositReturned != null) chargeRows.push(['Deposit returned', money(rental.depositReturned)]);
  const chargeHtml = chargeRows.map(([k, v], i) => `<tr class="${i === chargeRows.length - 1 || k === 'Total' ? 'bold' : ''}"><td class="k">${esc(k)}</td><td class="num">${esc(v)}</td></tr>`).join('');
  const depReasonHtml = rental.depositReason ? `<p class="reason"><b>Reason for deposit deduction:</b> ${esc(rental.depositReason)}</p>` : '';
  const chargeReasonHtml = rental.chargeReason ? `<p class="reason"><b>Note:</b> ${esc(rental.chargeReason)}</p>` : '';

  const clausesHtml = liabilityClauses(ctx)
    .map(([h, t], i) => `<div class="clause"><b>${i + 1}. ${esc(h)}.</b> ${esc(t)}</div>`).join('');

  const tollList = rental.tolls || [];
  const tollSection = (tollList.length || rental.tollEst != null)
    ? `<h2>Tolls (estimated)</h2>${tollList.length
        ? `<table>${tollList.map((t) => `<tr><td class="k">${esc(t.road)}</td><td>${esc(new Date(t.at).toLocaleString())}</td></tr>`).join('')}<tr class="bold"><td class="k">Estimated toll total</td><td class="num">${money(rental.tollEst || 0)}</td></tr></table>`
        : `<p>${money(rental.tollEst || 0)} (estimated)</p>`}<p class="empty">Estimated from GPS + road data — not the exact toll authority charge.</p>`
    : '';

  const milesLine = totals.milesDriven != null
    ? `${Math.round(totals.milesDriven)} mi driven of ${totals.allowedMiles} allowed${totals.overageMiles ? ` · ${Math.round(totals.overageMiles)} over` : ''}`
    : `${totals.allowedMiles} mi allowance`;

  const photoCells = Object.keys(PHOTO_LABELS)
    .filter((slot) => carPhotos && carPhotos[slot])
    .map((slot) => `<figure><img src="${carPhotos[slot]}" alt="${PHOTO_LABELS[slot]}"/><figcaption>${PHOTO_LABELS[slot]}</figcaption></figure>`)
    .join('');
  const photoSection = photoCells
    ? `<h2>Vehicle photos</h2><div class="photos">${photoCells}</div>`
    : `<h2>Vehicle photos</h2><p class="empty">No vehicle photos on file. Add them in the car profile (Front / Left / Right / Back / Interior) and they will appear here.</p>`;

  const licenseSection = licensePhoto
    ? `<div class="sigbox"><div class="siglabel">Driver's license</div><img class="license" src="${licensePhoto}" alt="license"/></div>`
    : `<div class="sigbox"><div class="siglabel">Driver's license</div><div class="placeholder">No license photo on file. Capture it in the rental agreement.</div></div>`;
  const customerSection = customerPhoto
    ? `<div class="sigbox"><div class="siglabel">Renter with the vehicle</div><img class="license" src="${customerPhoto}" alt="renter with vehicle"/></div>`
    : '';
  const signatureSection = signature
    ? `<div class="sigbox"><div class="siglabel">Renter signature</div><img class="sig" src="${signature}" alt="signature"/><div class="sigmeta">${esc(ctx.renter || '')}${signedAt ? ` · signed ${esc(new Date(signedAt).toLocaleString())}` : ''}</div></div>`
    : `<div class="sigbox"><div class="siglabel">Renter signature</div><div class="sigline"></div><div class="sigmeta">Sign: _______________________  Date: __________</div></div>`;

  const statusBadge = rental.status === 'closed' ? 'CLOSED' : (totals.overdue ? 'OVERDUE' : 'ACTIVE');

  // concise text for sharing (Web Share / SMS / email) — the full legal doc is the PDF
  const shareText = [
    `${company} — Rental Agreement`,
    ctx.renter ? `Renter: ${ctx.renter}` : '',
    `Vehicle: ${ctx.vehicle || ''}${ctx.plate ? ` (${ctx.plate})` : ''}`,
    `Period: ${ctx.startAt ? new Date(ctx.startAt).toLocaleString() : ''} → ${ctx.dueAt ? new Date(ctx.dueAt).toLocaleString() : ''} (${ctx.days || 0} day(s))`,
    `Total: ${money(totals.total)}${totals.balanceDue > 0 ? ` · Balance due ${money(totals.balanceDue)}` : ' · Paid in full'}`,
    signedAt ? `Signed: ${new Date(signedAt).toLocaleString()}` : '',
    '',
    'This confirms your rental terms. You are responsible for the vehicle and for any damage, tickets, tolls, fuel, and charges per the signed agreement. A full copy is available on request.',
  ].filter(Boolean).join('\n');

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Rental Agreement — ${esc(ctx.renter || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; padding: 32px; font-size: 12px; line-height: 1.5; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0066ff; padding-bottom: 12px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: 800; }
  .sub { color: #64748b; font-size: 12px; }
  .badge { font-weight: 800; font-size: 11px; padding: 4px 12px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .5px; color: #334155; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin: 20px 0 8px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 4px 6px; vertical-align: top; border-bottom: 1px solid #f1f5f9; }
  td.k { color: #64748b; width: 42%; font-weight: 600; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.bold td { font-weight: 800; border-top: 1px solid #cbd5e1; }
  .cols { display: flex; gap: 24px; }
  .cols > div { flex: 1; }
  .clause { margin-bottom: 7px; }
  .reason { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 6px; padding: 6px 8px; }
  .sigrow { display: flex; gap: 24px; margin-top: 16px; flex-wrap: wrap; }
  .sigbox { flex: 1; min-width: 240px; }
  .siglabel { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
  .sig { max-height: 90px; border: 1px solid #cbd5e1; border-radius: 8px; background: #fff; }
  .license { max-width: 100%; max-height: 190px; border: 1px solid #cbd5e1; border-radius: 8px; }
  .sigline { height: 60px; border-bottom: 1px solid #0f172a; }
  .sigmeta { font-size: 11px; color: #475569; margin-top: 4px; }
  .photos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  figure { margin: 0; }
  figure img { width: 100%; height: 170px; object-fit: cover; border-radius: 8px; border: 1px solid #e2e8f0; }
  figcaption { font-size: 10px; font-weight: 700; color: #64748b; text-align: center; margin-top: 2px; }
  .miles { color: #334155; margin: 4px 0 0; }
  .empty { color: #94a3b8; font-style: italic; }
  .placeholder { border: 1px dashed #cbd5e1; border-radius: 8px; padding: 20px 12px; color: #94a3b8; font-style: italic; text-align: center; }
  .noprint { margin-bottom: 16px; }
  button { background: #0066ff; color: #fff; border: none; border-radius: 8px; padding: 10px 18px; font-weight: 700; cursor: pointer; margin-right: 8px; }
  button.share { background: #16a34a; }
  @media print { .noprint { display: none; } body { padding: 0; } h2 { page-break-after: avoid; } figure, .sigbox, .clause { page-break-inside: avoid; } }
</style>
<script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
<script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
</head><body>
  <div class="noprint">
    <button onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="share" onclick="shareDoc()">📤 Share (text / email / more)</button>
  </div>
  <div class="top">
    <div>
      <div class="title">${esc(company)}</div>
      <div class="sub">Rental Agreement · ${esc(rental.id || '')} · generated ${esc(new Date().toLocaleString())}</div>
    </div>
    <div class="badge">${statusBadge}</div>
  </div>

  <div class="cols">
    <div><h2>Rental terms</h2><table>${termRows}</table><p class="miles">🛣️ ${esc(milesLine)}</p></div>
    <div><h2>Charges</h2><table>${chargeHtml}</table>${chargeReasonHtml}${depReasonHtml}</div>
  </div>

  ${tollSection}

  <h2>Renter responsibility &amp; liability</h2>
  ${clausesHtml}
  <p style="margin-top:10px">By signing below, the renter acknowledges they have read, understood, and agree to this agreement in full, and accept liability for the vehicle and all charges described above.</p>

  <div class="sigrow">
    ${licenseSection}
    ${customerSection}
    ${signatureSection}
  </div>

  ${photoSection}

  <script>
    var SHARE = ${JSON.stringify(shareText)};
    var PHONE = ${JSON.stringify(ctx.phone || '')};
    var EMAIL = ${JSON.stringify(ctx.email || '')};
    var FILENAME = 'rental-agreement.pdf';
    async function buildPdfBlob(){
      var canvas = await html2canvas(document.body, { scale: 2, backgroundColor: '#ffffff', useCORS: true,
        ignoreElements: function(el){ return el.classList && el.classList.contains('noprint'); } });
      var img = canvas.toDataURL('image/jpeg', 0.92);
      var pdf = new jspdf.jsPDF('p', 'pt', 'a4');
      var pw = pdf.internal.pageSize.getWidth();
      var ph = pdf.internal.pageSize.getHeight();
      var imgH = canvas.height * pw / canvas.width;
      var pos = 0;
      pdf.addImage(img, 'JPEG', 0, pos, pw, imgH);
      var left = imgH - ph;
      while (left > 0) { pos = pos - ph; pdf.addPage(); pdf.addImage(img, 'JPEG', 0, pos, pw, imgH); left -= ph; }
      return pdf.output('blob');
    }
    async function shareDoc(){
      var blob = null;
      try { blob = await buildPdfBlob(); } catch(e){ blob = null; }
      if (blob) {
        try {
          var file = new File([blob], FILENAME, { type: 'application/pdf' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Rental Agreement' }); return;
          }
        } catch(e){ if (e && e.name === 'AbortError') return; }
        // fallback: download the real PDF so it can be attached to a text/email
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a'); a.href = url; a.download = FILENAME; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      // last-resort text share if the PDF couldn't be built
      if (navigator.share) { try { await navigator.share({ title: 'Rental Agreement', text: SHARE }); return; } catch(e){ if (e && e.name === 'AbortError') return; } }
      var how = prompt('Share by — type: text, email, or copy', 'text');
      if (how === null) return; how = how.trim().toLowerCase();
      if (how === 'text') { window.location.href = 'sms:' + (PHONE || '') + '?&body=' + encodeURIComponent(SHARE); }
      else if (how === 'email') { window.location.href = 'mailto:' + (EMAIL || '') + '?subject=' + encodeURIComponent('Your rental agreement') + '&body=' + encodeURIComponent(SHARE); }
      else { try { await navigator.clipboard.writeText(SHARE); alert('Copied.'); } catch(e){ alert(SHARE); } }
    }
    window.addEventListener('load', function(){ setTimeout(function(){ try { window.print(); } catch(e){} }, 500); });
  </script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to open the printable rental document.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
